/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import * as React from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'

import {
  Typography,
  withStyles,
  Theme,
  createStyles,
  WithStyles,
  CircularProgress,
  Grid
} from '@material-ui/core'
import { useCallback, useEffect, useRef, useState } from 'react'

import { debounce } from 'debounce'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../OneplaceLibraryProvider'
import { IIDAndName } from '../../models/Entity'
import { SelectOptions } from './types'

const styles = (_theme: Theme) => createStyles({})

const PAGE_SIZE = 100

interface IHazardAutoCompleteProps
  extends IOneplaceLibraryContextProp,
    WithStyles<typeof styles> {
  handleHazardChange: (event: any, newValue: IIDAndName | undefined) => void
  initialSelection?: IIDAndName | null
  selectionLists?: SelectOptions | []
  isOffline: boolean
}

export const HazardAutoComplete = withStyles(styles)(
  withOneplaceLibraryContext((props: IHazardAutoCompleteProps) => {
    const t = props.ctx.i18next.t

    const inputRef = useRef<HTMLInputElement>(null)

    const [hazards, setHazards] = useState<IIDAndName[]>([])
    const [selectedHazard, setSelectedHazard] = useState<
      IIDAndName | undefined
    >(props.initialSelection || undefined)
    const [loading, setLoading] = useState<boolean>(false)
    const [page, setPage] = useState<number>(0)
    const [hasMore, setHasMore] = useState<boolean>(true)
    const [inputValue, setInputValue] = useState('')
    const [open, setOpen] = useState(false)

    // eslint-disable-next-line @typescript-eslint/require-await
    const fetchData = async (name: string, from: number) => {
      try {
        setLoading(true)
        if (props.isOffline) {
          const hazardsList = props.selectionLists || []
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const mappedHazards: IIDAndName[] = hazardsList.map(
            (hazard: any) => ({
              id: hazard.value,
              name: hazard.label
            })
          )
          setHazards(mappedHazards)
        } else {
          const response = await props.ctx.client.searchHazardByName(
            name,
            from,
            PAGE_SIZE,
            props.ctx.client.getCapabilities().franchiseId
          )
          const resultSize = response.length
          setHazards((prev) => (from === 0 ? response : [...prev, ...response]))
          setHasMore(resultSize === PAGE_SIZE)
        }
      } catch (error) {
        console.error('Error loading Hazards:', error)
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      const name = inputValue
      void fetchData(name, page * PAGE_SIZE)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, page])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchDataDebounced = useCallback(
      debounce((name: string, from: number) => fetchData(name, from), 1000),
      []
    )

    const handleInputChange = (newValue: string) => {
      setInputValue(newValue)
      setPage(0)
      fetchDataDebounced(newValue, 0)
    }

    const handleScroll = (e: any) => {
      const bottom =
        e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight
      if (bottom && !loading && hasMore) {
        setPage((prevPage) => prevPage + 1)
      }
    }

    const handleChange = (event: any, newValue: IIDAndName | null) => {
      const valueToSend = newValue || undefined
      setSelectedHazard(valueToSend)
      props.handleHazardChange(event, valueToSend)
      setInputValue(newValue ? newValue.name : '')
    }

    // Effect to update inputValue and selectedHazard when initialSelection changes
    useEffect(() => {
      if (props.initialSelection) {
        setSelectedHazard(props.initialSelection)
        setInputValue(props.initialSelection.name)
      }
    }, [props.initialSelection])

    return (
      <Grid item xs={12} sm={6}>
        <Autocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          fullWidth
          id='hazard-search'
          noOptionsText={`No ${t(
            'customLabel_hazards'
          )} found matching your search`}
          loading={loading}
          inputValue={inputValue}
          options={hazards}
          value={selectedHazard}
          disableClearable
          onChange={handleChange}
          getOptionSelected={(option: IIDAndName, value: IIDAndName) =>
            option.id === value.id
          }
          getOptionLabel={(option) => option.name || ''}
          renderOption={(option) => <Typography>{option.name}</Typography>}
          renderInput={(params) => (
            <TextField
              {...params}
              onBlur={() => setOpen(false)}
              variant='standard'
              label={t('customLabel_hazard') + ' Involved'}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleInputChange(e.target.value)}
              inputRef={inputRef}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color='primary' size={20} />}
                  </>
                )
              }}
            />
          )}
          ListboxProps={{
            onScroll: handleScroll
          }}
        />
      </Grid>
    )
  })
)
